import React, { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]'
].join(',');

let bodyLockCount = 0;
let previousBodyOverflow = '';
let previousBodyPaddingRight = '';

function lockBodyScroll() {
  if (bodyLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
  bodyLockCount += 1;
}

function unlockBodyScroll() {
  bodyLockCount = Math.max(0, bodyLockCount - 1);
  if (bodyLockCount === 0) {
    document.body.style.overflow = previousBodyOverflow;
    document.body.style.paddingRight = previousBodyPaddingRight;
  }
}

function focusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => (
    !element.hasAttribute('disabled')
    && !element.hasAttribute('hidden')
    && element.getAttribute('aria-hidden') !== 'true'
  ));
}

export interface AccessibleModalProps {
  children: React.ReactNode;
  onClose: () => void;
  ariaLabelledBy: string;
  ariaDescribedBy?: string;
  backdropClassName?: string;
  backdropStyle?: React.CSSProperties;
  dialogClassName?: string;
  dialogStyle?: React.CSSProperties;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  initialFocusSelector?: string;
  ariaBusy?: boolean;
}

export default function AccessibleModal({
  children,
  onClose,
  ariaLabelledBy,
  ariaDescribedBy,
  backdropClassName,
  backdropStyle,
  dialogClassName,
  dialogStyle,
  closeOnBackdrop = true,
  closeOnEscape = true,
  initialFocusSelector,
  ariaBusy
}: AccessibleModalProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement ? document.activeElement : null
  );
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const initialFocusSelectorRef = useRef(initialFocusSelector);

  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscapeRef.current = closeOnEscape;
  }, [closeOnEscape, onClose]);

  useEffect(() => {
    lockBodyScroll();

    const currentLayer = backdropRef.current;
    const returnFocus = returnFocusRef.current;
    const coveredLayers = Array.from(document.querySelectorAll<HTMLElement>('[data-accessible-modal-layer="true"]'))
      .filter((layer) => layer !== currentLayer);
    const coveredLayerState = coveredLayers.map((layer) => ({
      layer,
      ariaHidden: layer.getAttribute('aria-hidden'),
      hadInert: layer.hasAttribute('inert')
    }));
    coveredLayers.forEach((layer) => {
      layer.setAttribute('aria-hidden', 'true');
      layer.setAttribute('inert', '');
    });

    const dialog = dialogRef.current;
    const preferredFocus = initialFocusSelectorRef.current
      ? dialog?.querySelector<HTMLElement>(initialFocusSelectorRef.current)
      : null;
    const firstFocus = dialog ? focusableElements(dialog)[0] : null;
    (preferredFocus || firstFocus || dialog)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      const modalLayers = document.querySelectorAll('[data-accessible-modal-layer="true"]');
      const isTopmost = modalLayers[modalLayers.length - 1] === backdropRef.current;
      if (!isTopmost) return;

      if (event.key === 'Escape' && closeOnEscapeRef.current) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = focusableElements(dialogRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      coveredLayerState.forEach(({ layer, ariaHidden, hadInert }) => {
        if (ariaHidden === null) layer.removeAttribute('aria-hidden');
        else layer.setAttribute('aria-hidden', ariaHidden);
        if (!hadInert) layer.removeAttribute('inert');
      });
      unlockBodyScroll();
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, []);

  return (
    <div
      ref={backdropRef}
      className={backdropClassName}
      style={backdropStyle}
      data-accessible-modal-layer="true"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={dialogRef}
        className={dialogClassName}
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-busy={ariaBusy || undefined}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
